/**
 * The member hub (Member Hub design, section 1) — the pure rules behind the shell and the
 * Overview: the tab list, the "View as visitor" gate, the one "Up next" step, the hero's fact
 * line, the rail's leaderboard and recent-activity cards. No fetch, no React; unit-tested in
 * `tests/community-hub.spec.ts`.
 *
 * Every figure is DealHunt's (the member payload, the summary, `/me`); this module only picks
 * and words. Imports `model.ts` and `gamification.ts`, never the reverse.
 */
import {
  BADGE_LOOKS,
  badgeLimitedAction,
  badgeProgressRows,
  badgeScarcity,
  badgeSoldOut,
  badgeStepAction,
  badgeStepsLine,
  closestBadges,
  foundingMemberPinned,
  firstReviewAwardLine,
  helpfulCount,
  reviewHref,
  sentCount,
  slugFromProductHref,
  tierLabel,
  utcDay,
  type BadgeCap,
  type BadgeLimitedAction,
  type BadgeProgressRow,
  type BadgeStepAction,
  type BadgeStepKey,
  type MemberHubPage,
} from './model'
import {
  conversationStarterPending,
  pendingHeadline,
  pendingSentence,
  type StarterPending,
} from './gamification'
import { ownNoteControls } from 'lib/owner-notes/band-copy'
import { withSlash } from 'lib/dealhunt/url'
import { OWNER_VIEWS_MIN_SHOWN, type OwnerShelfItem } from 'lib/dealhunt/types'
import type {
  CommunityBadgeId,
  CommunityFeedItem,
  CommunityMemberDiscussion,
  CommunityMemberResponse,
  CommunityPoints,
  CommunityProduct,
  CommunityStanding,
} from './types'

// ── tabs ──────────────────────────────────────────────────────────────────────

export interface HubTab {
  id: Exclude<MemberHubPage, 'badges'>
  label: string
  /** Shown to the member themself only — never rendered for a visitor (not disabled: absent),
   *  so never in the shared-cached HTML. */
  ownerOnly: boolean
}

/**
 * The hub's tabs, in page order — only tabs whose pages exist. Stats is public; Notifications
 * and Settings are owner-only; the Badges page is not a tab (the Overview's "All badges →" opens it).
 */
export const HUB_TABS: readonly HubTab[] = [
  { id: 'overview', label: 'Overview', ownerOnly: false },
  { id: 'gear', label: 'Gear', ownerOnly: false },
  // Public (Member Stats contract §0.1): the visitor and the member read the same numbers; only
  // the coaching inside the tab is the member's own.
  { id: 'stats', label: 'Stats', ownerOnly: false },
  { id: 'notifications', label: 'Notifications', ownerOnly: true },
  { id: 'settings', label: 'Settings', ownerOnly: true },
]

/** The tabs a reader sees: owner-only ones for the member themself only. */
export function hubTabs(own: boolean, tabs: readonly HubTab[] = HUB_TABS): HubTab[] {
  return tabs.filter(tab => own || !tab.ownerOnly)
}

/** "Notifications and Settings are only visible to you" — worded
 *  from the owner-only tabs shown; null when none is (a visitor, "View as visitor"). */
export function ownerTabsNote(shown: readonly HubTab[]): string | null {
  const names = shown.filter(tab => tab.ownerOnly).map(tab => tab.label)
  if (names.length === 0) {
    return null
  }
  const list =
    names.length === 1
      ? names[0]
      : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`
  return `${list} ${names.length === 1 ? 'is' : 'are'} only visible to you`
}

/** The tab marked as where the reader is: the page's own tab; the Badges page sits under the
 *  Overview (it is the Overview's "All badges" drill-down), so Overview stays marked there. */
export function activeHubTab(page: MemberHubPage): HubTab['id'] {
  return page === 'badges' ? 'overview' : page
}

/** "Gear 2" — the public review count; "Notifications 3" — the member's unread figure (the
 *  bell's badge, owner view only; null until read). No figure at 0 (a zero is not drawn,
 *  5788508a). */
export function hubTabCount(
  tab: HubTab,
  member: Pick<CommunityMemberResponse, 'counts'>,
  unread: { count: number; capped: boolean } | null = null
): number | '99+' | null {
  if (tab.id === 'notifications') {
    if (!unread || !unread.count) {
      return null
    }
    // The server clamps the figure and says so (`capped`); the badge's "99+" rule.
    return unread.capped ? '99+' : unread.count
  }
  if (tab.id !== 'gear') {
    return null
  }
  const n = sentCount(member.counts.reviews)
  return n ? n : null
}

// ── the visitor preview ───────────────────────────────────────────────────────

/**
 * The member's own `/me` as the owner layers see it: null while "View as visitor" is on, so
 * every owner layer (Up next, the hero actions, the own badge view, owner-only tabs) renders
 * exactly the visitor page. The one gate — `useOwnMemberMe` applies it.
 */
export function viewerMe<T>(me: T | null, asVisitor: boolean): T | null {
  return asVisitor ? null : me
}

// ── the hero ──────────────────────────────────────────────────────────────────

/**
 * "Joined Sep 2026 · 2 owner reviews · 1 discussion · 3 replies · 4 Helpful": DealHunt's date and
 * counts. A sent 0 is DealHunt's figure and prints (design 1a: "0 owner reviews"); discussions,
 * replies and Helpful are left out when the payload does not carry them, "Joined" when the date
 * is unreadable. Replies are every reply (`replies_all`, 2026-10-07), else the rewarded count an
 * older DealHunt sends.
 */
export function heroFacts(
  member: Pick<CommunityMemberResponse, 'counts'>,
  joined: string
): string[] {
  const facts: string[] = []
  if (joined) {
    facts.push(`Joined ${joined}`)
  }
  const reviews = sentCount(member.counts.reviews)
  if (reviews !== null) {
    facts.push(
      reviews === 1 ? '1 owner review' : `${reviews.toLocaleString('en-US')} owner reviews`
    )
  }
  const discussions = sentCount(member.counts.discussions)
  if (discussions !== null) {
    facts.push(
      discussions === 1 ? '1 discussion' : `${discussions.toLocaleString('en-US')} discussions`
    )
  }
  const replies = sentCount(member.counts.replies_all) ?? sentCount(member.counts.replies)
  if (replies !== null) {
    facts.push(replies === 1 ? '1 reply' : `${replies.toLocaleString('en-US')} replies`)
  }
  const helpful = sentCount(member.counts.helpful)
  if (helpful !== null) {
    facts.push(`${helpful.toLocaleString('en-US')} Helpful`)
  }
  return facts
}

/** "50 to Contributor", or null at the top tier (no line then). */
export function toNextTierLine(
  next: { id: string; points_to_go: number } | null | undefined
): string | null {
  return next ? `${next.points_to_go.toLocaleString('en-US')} to ${tierLabel(next.id)}` : null
}

// ── Up next ───────────────────────────────────────────────────────────────────

/** The one act the Up next card offers: a phase-1 badge action (`BadgeAction`) or a link to the
 *  member's waiting discussion. */
export type UpNextCta =
  | { kind: 'badge'; action: Exclude<BadgeStepAction, 'others'> | BadgeLimitedAction }
  | { kind: 'discussion'; item: StarterPending['items'][number] }

export interface UpNext {
  /** Why this one won (`chooseUpNext`'s precedence) — tests and the eyebrow's tone. */
  reason: 'one_action' | 'closest' | 'tier' | 'pending'
  /** The badge it is about; null for the tier and the pending step. */
  badgeId: CommunityBadgeId | null
  title: string
  body: string
  cta: UpNextCta
  /** "+20 on the way · …" — the member's Conversation Starter waiting for a reply, said under
   *  any other step (design 1a); null when nothing waits or it IS the step. */
  pending: { headline: string; sentence: string } | null
}

/** What `chooseUpNext` reads — every figure a payload's. */
export interface UpNextInput {
  /** The member's own standing (`/me` community), own page only. */
  standing: CommunityStanding | null
  /** The member's badges (`knownBadges`), the same list the badges block draws. */
  badges: ReadonlyArray<{
    id: CommunityBadgeId
    earned: boolean
    progress?: ReadonlyArray<{ key: string; have: unknown; need: unknown }> | null
  }>
  /** The summary's caps (`badgeFacts`). */
  caps: Partial<Record<CommunityBadgeId, BadgeCap>>
  /** The summary's points per owner review (`points.review`); null without a summary. */
  reviewPoints: number | null
}

/** The pinned head of Closest to unlock — Founding Member while `/me` says it can still be had,
 *  then a Conversation Starter waiting for a reply. ONE builder: the badges block and Up next
 *  read the same order. Up next may still lead with a badge the block ranks lower when ONE act
 *  completes it (`chooseUpNext` step 1 — the single most achievable step wins). */
export function closestPinned(standing: CommunityStanding | null): CommunityBadgeId[] {
  const pinned: CommunityBadgeId[] = []
  if (foundingMemberPinned(standing)) {
    pinned.push('founding_member')
  }
  if (conversationStarterPending(standing) !== null) {
    pinned.push('conversation_starter')
  }
  return pinned
}

/** "+50 pts" may be promised for the next owner review only when DealHunt says a review can
 *  still earn today (`reviews_today_left` > 0) and sent the table's figure. Absent = unknown =
 *  no figure; 0 = the daily cap is reached. */
function reviewPointsNow(input: UpNextInput): number | null {
  return reviewPointsToday(input.standing, input.reviewPoints)
}

/** The same rule for any hub surface that promises a NEW review's points (Up next, the Gear
 *  tab's "Add gear you own" and its product tiles): DealHunt's `points.review` while `/me` says a
 *  review still earns today. One rule per page — the Gear CTA never promises what Up next won't. */
export function reviewPointsToday(
  standing: Pick<CommunityStanding, 'reviews_today_left'> | null | undefined,
  reviewPoints: unknown
): number | null {
  const left = sentCount(standing?.reviews_today_left)
  const points = sentCount(reviewPoints)
  return left && points ? points : null
}

/** ", +50 pts and Contributor" / " and +50 pts" / "" — what one owner review adds besides the
 *  badge. The tier is named only when both figures are DealHunt's and the review's points reach
 *  it (`points.review` ≥ `next_tier.points_to_go`). */
function reviewEffects(input: UpNextInput): string {
  const points = reviewPointsNow(input)
  if (points === null) {
    return ''
  }
  const next = input.standing?.next_tier
  const pts = `+${points.toLocaleString('en-US')} pts`
  return next && points >= next.points_to_go ? `, ${pts} and ${tierLabel(next.id)}` : ` and ${pts}`
}

/** "One <act> earns …", keyed by the PROGRESS STEP, not the action: the same act (an owner
 *  review) counts differently per step — Cross-Category counts only a review in a category the
 *  member has not reviewed yet. A step without words here (Helpful: other readers give it) is
 *  never a one-act step. Digit-free: every threshold is DealHunt's. */
const ONE_STEP: Readonly<Partial<Record<BadgeStepKey, string>>> = {
  reviews: 'One owner review',
  categories: 'One owner review in a category you haven’t reviewed yet',
  replies: 'One reply to another member’s review or discussion',
  updates: 'One six-month update to a review, once its update opens',
}

/** The steps whose act is an owner review — the only ones "+N pts" (`points.review`) is promised
 *  for. */
const REVIEW_STEPS: ReadonlySet<BadgeStepKey> = new Set(['reviews', 'categories'])

/** The first unfinished row a member can act on (not the "others give it" Helpful row). */
function nextActionableRow(
  rows: readonly BadgeProgressRow[]
): { row: BadgeProgressRow; action: Exclude<BadgeStepAction, 'others'> } | null {
  for (const row of rows) {
    const action = badgeStepAction(row.key)
    if (!row.done && action !== 'others') {
      return { row, action }
    }
  }
  return null
}

/**
 * The ONE step the Up next card offers the member on their own page (design 1a), or null.
 * Precedence — the first that applies wins:
 *
 * 1. **One action completes a badge** — the single most achievable step (design 1a), so it WINS
 *    over Closest to unlock's rank: Early Owner while `/me` says the next review WOULD take a
 *    number (`founding_next`) and the summary says numbers are left; then any badge in Closest to
 *    unlock whose only missing step is ONE review / review in a new category / reply / update
 *    (`ONE_STEP`), even when the block lists another badge first.
 * 2. **Closest ratio.** The first badge in Closest to unlock's order (the pinned Founding Member,
 *    a waiting Conversation Starter, then the furthest along) that has a step the member can act
 *    on — a pinned badge with only Helpful left, or with no rows at all, is passed over.
 * 3. **Next tier.** "X pts to <Tier>" from `next_tier.points_to_go`; an owner review is the act.
 * 4. **Points on the way.** Nothing else to do: the waiting Conversation Starter, linking its
 *    discussion.
 *
 * Under steps 1–3 the waiting Conversation Starter rides along as one line (`pending`). Copy may
 * add "+N pts" (the summary's `points.review`) only while `/me` says a review still earns today,
 * and name the tier only when both figures say one review reaches it.
 */
export function chooseUpNext(input: UpNextInput): UpNext | null {
  const { standing, badges, caps } = input
  if (!standing) {
    return null
  }
  const starter = conversationStarterPending(standing)
  const pending = starter
    ? { headline: pendingHeadline(starter), sentence: pendingSentence(starter) }
    : null
  const closest = closestBadges(badges, { limit: 3, pinned: closestPinned(standing), caps })

  // 1a. Early Owner: one owner review takes a number, says DealHunt.
  const early = badges.find(b => b.id === 'founding_owner' && !b.earned)
  const earlyCap = caps.founding_owner
  if (
    early &&
    earlyCap &&
    sentCount(standing.founding_next) &&
    !badgeSoldOut('founding_owner', caps)
  ) {
    return {
      reason: 'one_action',
      badgeId: 'founding_owner',
      title: `${BADGE_LOOKS.founding_owner.name} · ${badgeScarcity(earlyCap).left}`,
      body: `One owner review earns the badge${reviewEffects(input)}.`,
      cta: { kind: 'badge', action: badgeLimitedAction('founding_owner') ?? 'review' },
      pending,
    }
  }

  // 1b. A badge one act away: every other step done, the missing one short by exactly one.
  for (const { badge } of closest) {
    const rows = badgeProgressRows(badge)
    const open = rows?.filter(row => !row.done) ?? []
    const next = rows ? nextActionableRow(rows) : null
    const words = next ? ONE_STEP[next.row.key] : undefined
    if (open.length === 1 && next && next.row.need - next.row.have === 1 && words) {
      const effects = REVIEW_STEPS.has(next.row.key) ? reviewEffects(input) : ''
      return {
        reason: 'one_action',
        badgeId: badge.id,
        title: BADGE_LOOKS[badge.id].name,
        body: `${words} earns the badge${effects}.`,
        cta: { kind: 'badge', action: next.action },
        pending,
      }
    }
  }

  // 2. The first badge in Closest to unlock's order with a step the member can take.
  for (const { badge } of closest) {
    const rows = badgeProgressRows(badge)
    const next = rows ? nextActionableRow(rows) : null
    if (rows && next) {
      const steps = badgeStepsLine(rows)
      return {
        reason: 'closest',
        badgeId: badge.id,
        title: `${BADGE_LOOKS[badge.id].name} · ${steps ?? next.row.value}`,
        body: `Next: ${next.row.label.toLowerCase()} ${next.row.value}.`,
        cta: { kind: 'badge', action: next.action },
        pending,
      }
    }
  }

  // 3. The next tier.
  const next = standing.next_tier
  if (next) {
    const points = reviewPointsNow(input)
    return {
      reason: 'tier',
      badgeId: null,
      title: `${next.points_to_go.toLocaleString('en-US')} pts to ${tierLabel(next.id)}`,
      body: points
        ? `An owner review adds +${points.toLocaleString('en-US')} pts.`
        : 'Owner reviews, replies and Helpful votes all count.',
      cta: { kind: 'badge', action: 'review' },
      pending,
    }
  }

  // 4. Only the waiting discussion.
  if (starter && starter.items[0]) {
    return {
      reason: 'pending',
      badgeId: 'conversation_starter',
      title: pendingHeadline(starter),
      body: pendingSentence(starter),
      cta: { kind: 'discussion', item: starter.items[0] },
      pending: null,
    }
  }
  return null
}

// ── the rail ──────────────────────────────────────────────────────────────────

export interface RankCard {
  /** "#9", or "Not ranked yet". */
  allTime: string
  /** "#1" this month; null when unranked this month (no row). */
  month: string | null
  /** "Last place on the board: 50 pts" — `/summary.last_ranked_points.all`, display only
   *  (DealHunt 8e66e754: never an entry threshold); null when not sent. */
  lastPlace: string | null
  /** The member's own page while unranked: how to get on the board. */
  note: string | null
}

export const UNRANKED_NOTE =
  'Any points put you on the board — one owner review or one answer to a discussion.'

/**
 * The rail's Leaderboard card. `ranks` is the member payload's (public, the visitor and server
 * view) or `/me`'s (own page, after hydration — fresher than the cached page); `own` adds the
 * unranked note. N of "#N has X pts" is not sent on this page (`ranked_total` lives on
 * `/leaderboard` only), so the line names last place instead.
 */
export function rankCard(
  ranks: { rank_all: number | null; rank_month: number | null },
  lastRankedPoints: number | null,
  own: boolean
): RankCard {
  const all = sentCount(ranks.rank_all)
  const month = sentCount(ranks.rank_month)
  const last = sentCount(lastRankedPoints)
  return {
    allTime: all ? `#${all.toLocaleString('en-US')}` : 'Not ranked yet',
    month: month ? `#${month.toLocaleString('en-US')}` : null,
    lastPlace: last ? `Last place on the board: ${last.toLocaleString('en-US')} pts` : null,
    note: own && !all ? UNRANKED_NOTE : null,
  }
}

/**
 * A site path from the public member payload (a discussion's or a thread's `url`), on the app's
 * trailing-slash convention; null for anything that is not a same-site path (a scheme, `//host`,
 * junk) — the row is then named without a link. The payload lands in the visitor HTML.
 */
export function memberPayloadHref(url: unknown, trailingSlash: boolean): string | null {
  if (typeof url !== 'string' || !/^\/(?![/\\])/.test(url) || /\s/.test(url)) {
    return null
  }
  const cut = url.search(/[?#]/)
  const path = cut === -1 ? url : url.slice(0, cut)
  return withSlash(path, trailingSlash) + (cut === -1 ? '' : url.slice(cut))
}

/** "Sep 11" — a UTC day from a fixed month table (shared-cached HTML, bb38c2ea); with the year
 *  when it is not the render's year. */
function activityDay(iso: string, now: number): string | null {
  const at = Date.parse(iso)
  const sameYear =
    !Number.isNaN(at) && new Date(at).getUTCFullYear() === new Date(now).getUTCFullYear()
  return utcDay(iso, !sameYear)
}

function nonEmpty(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null
}

export interface ActivityRow {
  id: string
  /** "Wrote an owner review", "Asked a question", "Answered “<title>”"… */
  label: string
  /** Where the row goes; null = named without a link. */
  href: string | null
  /** The product (a review) or the thread's title. */
  title: string
  /** True when `label` already says the title (a reply): the row links the label and prints
   *  the date alone, so the title is said once. */
  titleInLabel: boolean
  date: string | null
  /** ms epoch, the sort key; 0 = undated (last). */
  at: number
}

/**
 * Recent activity: the member's latest owner reviews, discussions and replies (all from the public
 * payload — the visitor HTML), newest first, up to `limit`. Titles and dates only — the review
 * text appears once, on the Gear tab. `discussions` / `replies` absent (an older DealHunt) = the
 * reviews alone, exactly as before; a row of an unknown kind or without a title is skipped.
 */
export function recentActivity(
  member: Pick<CommunityMemberResponse, 'reviews' | 'discussions' | 'replies'>,
  now: number,
  trailingSlash: boolean,
  limit = 3
): ActivityRow[] {
  const rows: ActivityRow[] = []
  for (const item of member.reviews) {
    rows.push({
      id: `review:${item.note.id}`,
      label: 'Wrote an owner review',
      href: reviewHref(item, trailingSlash),
      title: item.product.name,
      titleInLabel: false,
      date: activityDay(item.note.published_at, now),
      at: Date.parse(item.note.published_at) || 0,
    })
  }
  for (const d of Array.isArray(member.discussions) ? member.discussions : []) {
    const title = nonEmpty(d?.title)
    if (!title || (d.kind !== 'question' && d.kind !== 'discussion')) {
      continue
    }
    rows.push({
      id: `discussion:${String(d.id)}`,
      label: d.kind === 'question' ? 'Asked a question' : 'Started a discussion',
      href: memberPayloadHref(d.url, trailingSlash),
      title,
      titleInLabel: false,
      date: activityDay(d.published_at, now),
      at: Date.parse(d.published_at) || 0,
    })
  }
  const replies = Array.isArray(member.replies) ? member.replies : []
  replies.forEach((r, i) => {
    const title = nonEmpty(r?.thread?.title)
    const kind = r?.thread?.kind
    if (!title || (kind !== 'review' && kind !== 'discussion')) {
      return
    }
    rows.push({
      id: `reply:${i}:${String(r.at)}`,
      label: kind === 'review' ? `Replied to a review of ${title}` : `Answered “${title}”`,
      href: memberPayloadHref(r.thread.url, trailingSlash),
      title,
      titleInLabel: true,
      date: activityDay(r.at, now),
      at: Date.parse(r.at) || 0,
    })
  })
  // Stable: equal instants keep reviews, discussions, replies in the payload's order.
  return rows.sort((a, b) => b.at - a.at).slice(0, limit)
}

// ── the Overview's Discussions block (2026-10-07) ─────────────────────────────

export interface MemberDiscussionRow {
  id: string
  /** "Question" / "Discussion". */
  kind: string
  title: string
  href: string | null
  /** "3 answers", "1 answer"; without a sent answer: "Waiting for an answer" (a question) or
   *  "No answer yet" (a discussion) — DealHunt's count is cached and can lag, so never "no
   *  answers" as a fact. */
  answers: string
  date: string | null
}

export const MEMBER_DISCUSSIONS_TITLE = 'Discussions'
export const OWN_DISCUSSIONS_EMPTY = 'You haven’t started a discussion yet.'
export const OWN_DISCUSSIONS_ASK = 'Ask the owners a question →'

export function discussionAnswersLine(kind: 'question' | 'discussion', count: unknown): string {
  const n = sentCount(count)
  if (n !== null && n > 0) {
    return n === 1 ? '1 answer' : `${n.toLocaleString('en-US')} answers`
  }
  return kind === 'question' ? 'Waiting for an answer' : 'No answer yet'
}

/**
 * The member's discussions block, from the public payload: null when the payload does not carry
 * the list (an older DealHunt — unknown, never "none"); else the rows (unknown kinds and untitled
 * rows skipped), at most 5, in the payload's order (newest first).
 */
export function memberDiscussionRows(
  discussions: readonly CommunityMemberDiscussion[] | undefined,
  now: number,
  trailingSlash: boolean
): MemberDiscussionRow[] | null {
  if (!Array.isArray(discussions)) {
    return null
  }
  const list = discussions as readonly CommunityMemberDiscussion[]
  const rows: MemberDiscussionRow[] = []
  for (const d of list) {
    const title = nonEmpty(d?.title)
    if (!title || (d.kind !== 'question' && d.kind !== 'discussion')) {
      continue
    }
    rows.push({
      id: String(d.id),
      kind: d.kind === 'question' ? 'Question' : 'Discussion',
      title,
      href: memberPayloadHref(d.url, trailingSlash),
      answers: discussionAnswersLine(d.kind, d.answer_count),
      date: activityDay(d.published_at, now),
    })
  }
  return rows.slice(0, 5)
}

// ── the Gear tab's owner layer (Member Hub 1b, phase 3) ───────────────────────

/** One row of the Gear list: the review as the row draws it, and the member's own shelf row
 *  when the reader is the member (null = a visitor's row, the shared HTML's). */
export interface GearListRow {
  item: CommunityFeedItem
  own: OwnerShelfItem | null
}

/**
 * A shelf row (`/me`) as a feed item, for a review the public payload does not carry — checking,
 * rejected, held or report-hidden, or live but newer than the 60 s shared copy. The shelf's own
 * text and labels (the revision the author sees). No product slug: a review that is not public
 * has no thread to link, and the leaf alone would 404 (#113). Reader-specific, never in the HTML.
 */
export function shelfFeedItem(own: OwnerShelfItem, handle: string): CommunityFeedItem {
  const { note } = own
  return {
    kind: 'owner_review',
    note: {
      id: note.id,
      node_id: note.node_id,
      handle: note.handle ?? handle,
      note: note.note,
      answers: note.answers,
      photo_status: note.photo_status,
      photo_label: note.photo_label,
      published_at: note.published_at ?? '',
      edited_at: note.edited_at,
      // The author's own photos (contract-review-photos §3) — public ones only, as DealHunt sends.
      ...(note.images ? { images: note.images } : {}),
      ...(typeof note.image_count === 'number' ? { image_count: note.image_count } : {}),
      ...(note.proofread === true ? { proofread: true } : {}),
    },
    labels: own.labels ?? { ownership_length: '', still_using: '', reads: [] },
    product: {
      node_id: own.node_id,
      name: own.node_name,
      slug: '',
      image_url: own.image_url,
      category: own.category ?? null,
    },
    author: { handle, member: null },
    ...(sentCount(own.helpful_count) !== null ? { helpful_count: own.helpful_count } : {}),
  }
}

/**
 * The Gear list. A visitor (`shelf` null): every public review, in the payload's order. The
 * member: ONE list built from the shelf — each shelf note once, with its status, a public row
 * with its public text (the live revision, the HTML's) — ordered by the member's most recent
 * activity first (`ownActivityAt`), so the note that needs them (just sent, rejected) is on top;
 * ties keep the payload's order, then the shelf's. A public row with no shelf note is dropped: the
 * shelf never carries a removed review and the member payload may, for its 60 s shared lifetime
 * (removed reviews stay invisible to their author — DealHunt contract #123).
 */
export function gearListRows(
  reviews: readonly CommunityFeedItem[],
  shelf: readonly OwnerShelfItem[] | null,
  handle: string
): GearListRow[] {
  if (!shelf) {
    return reviews.map(item => ({ item, own: null }))
  }
  const mine = shelf.filter(own => own.note.status !== 'removed')
  const byNote = new Map(mine.map(own => [own.note.id, own]))
  const rows: GearListRow[] = []
  const placed = new Set<string>()
  for (const item of reviews) {
    const own = byNote.get(item.note.id)
    if (own && !placed.has(own.note.id)) {
      placed.add(own.note.id)
      rows.push({ item, own })
    }
  }
  for (const own of mine) {
    if (!placed.has(own.note.id)) {
      placed.add(own.note.id)
      rows.push({ item: shelfFeedItem(own, handle), own })
    }
  }
  // Array#sort is stable: equal instants keep the order built above.
  // (No subtraction: two undated notes are both Infinity, and Infinity - Infinity is NaN.)
  return rows.sort((a, b) => {
    const at = ownActivityAt(a.own)
    const bt = ownActivityAt(b.own)
    if (at === bt) {
      return 0
    }
    return bt > at ? 1 : -1
  })
}

/**
 * When the member last acted on a note: `edited_at` (stamped on every submission, a rejected one
 * too — the author's activity, not the visible revision) else `published_at`. The shelf sends no
 * `created_at`, so a note with neither (a first submission still checking, rejected or held)
 * counts as the newest. Ordering only — never printed.
 */
function ownActivityAt(own: OwnerShelfItem | null): number {
  const iso = own?.note.edited_at || own?.note.published_at
  const at = iso ? Date.parse(iso) : NaN
  return Number.isNaN(at) ? Number.POSITIVE_INFINITY : at
}

export type GearStatusTone = 'live' | 'checking' | 'held'

/**
 * The status chip on the member's own row (design 1b): Live (published), Checking, and
 * Rejected / Held — red, "only you see this" — for rejected, held_retry, held_review and the
 * report-hidden note. No Removed state: the shelf never carries one (#123). An unknown status
 * reads as Checking, the band-copy rule (`ownNoteControls`).
 */
export function gearStatus(status: string): { label: string; tone: GearStatusTone } {
  if (status === 'published') {
    return { label: 'Live', tone: 'live' }
  }
  if (status === 'rejected') {
    return { label: 'Rejected · only you see this', tone: 'held' }
  }
  if (status === 'held_retry' || status === 'held_review' || status === 'hidden') {
    return { label: 'Held · only you see this', tone: 'held' }
  }
  return { label: 'Checking', tone: 'checking' }
}

/**
 * The row's write action: "Edit review" where the band may edit (`ownNoteControls` — never a
 * checking or report-hidden note, DealHunt 409s those), "Rewrite and resubmit" on a rejected
 * note while DealHunt leaves resubmits (`resubmits_left`), and at 0 resubmits still a resend that
 * a moderator checks (`resend` — DealHunt takes it as held_review, as My gear's card offers).
 */
export function gearEditAction(note: {
  status: string
  resubmits_left: number
}): 'edit' | 'resubmit' | 'resend' | null {
  if (!ownNoteControls(note.status).edit) {
    return null
  }
  if (note.status === 'rejected') {
    // At 0 resubmits DealHunt still takes a resend, held for a moderator (Guillaume 2026-10-05).
    return note.resubmits_left > 0 ? 'resubmit' : 'resend'
  }
  return 'edit'
}

/**
 * "312 readers · 4 Helpful · Updated Sep 11" under the member's own row. Readers and Helpful on
 * a LIVE note only: readers are the author's private count (`views.total`), shown from
 * `OWNER_VIEWS_MIN_SHOWN` like the own card; Helpful is the shelf's figure, else the public row's.
 * "Updated" = the row's `edited_at`, else `published_at` (the public row's when it has one: the
 * visible revision), with the year when it is not `now`'s. Null when nothing is known.
 */
export function gearOwnerMeta(row: GearListRow, now: number): string | null {
  const { item, own } = row
  if (!own) {
    return null
  }
  const parts: string[] = []
  if (own.note.status === 'published') {
    const readers = sentCount(own.views?.total)
    if (readers !== null && readers >= OWNER_VIEWS_MIN_SHOWN) {
      parts.push(`${readers.toLocaleString('en-US')} readers`)
    }
    const helpful = sentCount(own.helpful_count) ?? helpfulCount(item)
    if (helpful !== null) {
      parts.push(`${helpful.toLocaleString('en-US')} Helpful`)
    }
  }
  const updatedIso = item.note.edited_at || item.note.published_at || own.note.published_at
  const at = updatedIso ? Date.parse(updatedIso) : NaN
  if (!Number.isNaN(at)) {
    const sameYear = new Date(at).getUTCFullYear() === new Date(now).getUTCFullYear()
    parts.push(`Updated ${utcDay(updatedIso, !sameYear)}`)
  }
  return parts.length ? parts.join(' · ') : null
}

/**
 * The six-month update hint: "6-month update opens Mar 4 · +30" while DealHunt's
 * `update_opens_at` is ahead of `now` (the CLIENT clock, after mount — the field is not clamped),
 * "6-month update open now · +30" once it has passed. "+N" is the summary's
 * `points.review_update`, omitted without it. Null when the field is absent or null (not
 * published, already earned, never can) — SoundGuys computes no date.
 */
export function updateHint(
  opensAt: string | null | undefined,
  now: number,
  points: Pick<CommunityPoints, 'review_update'> | null | undefined
): string | null {
  if (typeof opensAt !== 'string') {
    return null
  }
  const at = Date.parse(opensAt)
  if (Number.isNaN(at)) {
    return null
  }
  const plus = sentCount(points?.review_update)
  const tail = plus ? ` · +${plus.toLocaleString('en-US')}` : ''
  if (at <= now) {
    return `6-month update open now${tail}`
  }
  const sameYear = new Date(at).getUTCFullYear() === new Date(now).getUTCFullYear()
  return `6-month update opens ${utcDay(opensAt, !sameYear)}${tail}`
}

/** The tiles shown under the Gear list and where they came from. */
export interface GearCandidates {
  /** `declared`: this browser's declared product (`sg_owner_declared`); `waiting`: DealHunt's
   *  products with no owner review yet (the summary's `waiting_products`). */
  kind: 'declared' | 'waiting'
  products: CommunityProduct[]
}

/** How many tiles the block shows (design 1b). */
export const GEAR_CANDIDATES_SHOWN = 4

/**
 * "Own one of these? No owner review yet." (design 1b): the product this browser declared it
 * owns, unless the member already has a note on it (canonical id and aliases, #110, or the same
 * name when the declaration has no id); without one, DealHunt's waiting products the member has
 * no note on, up to `GEAR_CANDIDATES_SHOWN`. Products without a SoundGuys slug are skipped (no
 * form to open). Null when nothing is left — the block is hidden.
 */
export function gearCandidates(
  declared: {
    nodeId: string | null
    href: string | null
    name: string
    imageUrl: string | null
  } | null,
  waiting: readonly CommunityProduct[],
  shelf: ReadonlyArray<Pick<OwnerShelfItem, 'node_id' | 'node_ids' | 'node_name'>>
): GearCandidates | null {
  const taken = new Set<string>()
  const names = new Set<string>()
  for (const own of shelf) {
    taken.add(own.node_id)
    own.node_ids?.forEach(id => taken.add(id))
    names.add(own.node_name.trim().toLowerCase())
  }
  const declaredSlug = declared ? slugFromProductHref(declared.href) : null
  if (declared && declaredSlug) {
    const onShelf = declared.nodeId
      ? taken.has(declared.nodeId)
      : names.has(declared.name.trim().toLowerCase())
    if (!onShelf) {
      return {
        kind: 'declared',
        products: [
          {
            node_id: declared.nodeId ?? declaredSlug,
            name: declared.name,
            slug: declaredSlug,
            image_url: declared.imageUrl,
            category: null,
          },
        ],
      }
    }
  }
  const products = waiting
    .filter(product => product.slug && !taken.has(product.node_id))
    .slice(0, GEAR_CANDIDATES_SHOWN)
  return products.length ? { kind: 'waiting', products } : null
}

/**
 * The tiles' points chip, only while a review still earns today (`reviewPointsToday`): "+75 each"
 * on waiting products — DealHunt's `review` plus its `first_review_bonus`, the feed module's
 * figure (they have no review yet); "+50" on the declared product, whose review count is unknown,
 * so no first-review bonus is promised (`reviewAward`). Null = no chip.
 */
export function gearCandidatesAward(
  kind: GearCandidates['kind'],
  points: CommunityPoints | null | undefined,
  standing: Pick<CommunityStanding, 'reviews_today_left'> | null | undefined
): string | null {
  const review = reviewPointsToday(standing, points?.review)
  if (review === null || !points) {
    return null
  }
  return kind === 'waiting' ? firstReviewAwardLine(points) : `+${review.toLocaleString('en-US')}`
}
